import { formatPct } from "../../../formatters"
import { Checkbox, classes, getSoilUnitLabel, isArrayEmpty, react, SoilUnit } from "../../../omnia"
import { SelectableStdAnalysis, StdAnalysisAlloc } from "../../analysis/std"
import { PlanContext } from "../../context"
import { PlanState, SelectedPlanField } from "../../types"
import { AnalysisValueGauge } from "./Gauge"
import { Header } from "./Header"

type Settings = PlanState["settings"]["analysis"]

interface Props {
    field: SelectedPlanField
    settings: Settings
}


export function StdAnalysisTable({ field: { analysis: { std } }, settings }: Props) {

    const { dispatch } = React.useContext(PlanContext)

    if (isArrayEmpty(std)) {
        return null
    }

    const multiple = std.some(hasMultipleAnalysis)
    const { expandGroups, unit } = settings

    const unitText = getSoilUnitLabel(unit)

    const setExpandGroups = (expandGroups: boolean) => {
        dispatch({ type: "updateAnalysisSettings", update: { expandGroups } })
    }

    return <Header title="Soil analysis" className="std">

        <table className="table omnia-table">

            <thead>
                <tr>
                    <th className="date">
                        <span>Date</span>
                        {multiple &&
                            <button type="button" className="btn btn-icon transparent" onClick={() => setExpandGroups(!expandGroups)} title="Show/hide zones">
                                <i className={classes(["fas", expandGroups ? "fa-angle-up" : "fa-angle-down"])}></i>
                            </button>
                        }

                    </th>
                    <th>Number</th>
                    <th>P<sub>2</sub>O<sub>5</sub></th>
                    <th>K<sub>2</sub>O</th>
                    <th>MgO</th>
                    <th>pH</th>
                    <th>Organic matter</th>
                </tr>
                <tr>
                    <th></th>
                    <th></th>
                    <th className="unit">{unitText}</th>
                    <th className="unit">{unitText}</th>
                    <th className="unit">{unitText}</th>
                    <th></th>
                    <th className="unit">%</th>
                </tr>

            </thead>

            <tbody>
                {
                    std.map(sa => <SelectableAnalysisRow key={sa.key} sa={sa} unit={unit} expanded={expandGroups} />)
                }
            </tbody>

        </table>

    </Header>

}


function SelectableAnalysisRow({ sa, unit, expanded }: { sa: SelectableStdAnalysis, unit: SoilUnit, expanded: boolean }) {

    const { dispatch } = React.useContext(PlanContext)

    const setSelected = (analysis: SelectableStdAnalysis, selected: boolean) => {
        dispatch({ type: "selectAnalysis", target: { type: "std", analysis }, selected })
    }

    const multiple = hasMultipleAnalysis(sa)

    return <>

        <tr>

            <td className="date">
                <Checkbox checked={sa.selected.value} setChecked={s => setSelected(sa, s)} icons={true}>
                    {sa.date.toLocaleDateString()}
                </Checkbox>
            </td>

            <AnalysisColumns sa={sa.field} unit={unit} />

        </tr>

        {multiple && expanded &&

            sa.items.map(a => <AnalysisRow key={a.stdAnalysisId} sa={a} unit={unit} />)

        }

    </>

}

function AnalysisRow({ sa, unit }: { sa: StdAnalysisAlloc, unit: SoilUnit }) {


    return <tr className="group-item">

        <td className="date">
            <span>
                {sa.samplingDate.toLocaleDateString()}
            </span>
        </td>

        <AnalysisColumns sa={sa} unit={unit} />

    </tr>

}

function AnalysisColumns({ sa, unit }: { sa: StdAnalysisAlloc, unit: SoilUnit }) {

    const morgan = sa?.morgan

    return <>

        <td>{sa?.number}</td>
        <td>
            <AnalysisValueGauge attr="P2O5" value={sa?.p2O5} unit={unit} morgan={morgan} />
        </td>
        <td>
            <AnalysisValueGauge attr="K2O" value={sa?.k2O} unit={unit} morgan={morgan} />
        </td>
        <td>
            <AnalysisValueGauge attr="MgO" value={sa?.mgO} unit={unit} morgan={morgan} />
        </td>
        <td>
            <AnalysisValueGauge attr="pH" value={sa?.pH} unit={0} />
        </td>
        <td>{formatPct(sa?.organicMatter, 1)}</td>
    </>

}

function hasMultipleAnalysis(sa: SelectableStdAnalysis) {
    return (!sa.field) || (sa.items.length > 1)
}
